/**
 * AriMessageActions — native copy control for every completed Ari response.
 * One-tap full-message copy (no browser text selection), mobile + desktop,
 * preserves line breaks, brief "Copied" confirmation. Shared by every Ari
 * chat surface (Command Center, Docked Panel, Insights Overlay, Gateway).
 */
import React, { useState, useCallback } from 'react';
import { Copy, Check } from 'lucide-react';

export const copyTextToClipboard = async (text) => {
  const value = String(text ?? '');
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch { /* fall through to legacy path */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

export const CopyMessageButton = ({ text, light = false, testId = 'ari-copy-btn' }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = useCallback(async (e) => {
    e.stopPropagation();
    if (!text) return;
    const ok = await copyTextToClipboard(text);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }
  }, [text]);
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid={testId}
      aria-label={copied ? 'Copied' : 'Copy message'}
      className={`mt-1 inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-1 rounded-md transition-colors ${
        light
          ? 'text-slate-300 hover:text-white hover:bg-white/10'
          : 'text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-700/60'
      }`}
    >
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

export default CopyMessageButton;
